import React, { useState, useEffect } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Divider,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Menu as MenuIcon,
  GridViewRounded as DashboardIcon,
  DriveEta as VehicleIcon,
  Loupe as AddIcon,
  NoCrashSharp as AllVehiclesIcon,
  Delete as TrashIcon,
  HandshakeOutlined as AgreementIcon,
  Receipt as InvoiceIcon,
  People as UsersIcon,
  LogoutRounded as LogoutIcon,
  ExpandLess,
  ExpandMore,
  CalendarToday, // for Upcoming MOTs
  Badge
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import logo from "../../images/headerlogin.png";
import motIcon from "../../images/motIcon.png";
import { postRequest } from "../../Api";
import { apiRoutes } from "../../Api/apiRoutes";
import { setLoader } from "../../Redux/Reducers/gernalSlice";
import { setUser } from "../../Redux/Reducers/authSlice";

const menuItems = [
  { label: "Dashboard", icon: <DashboardIcon />, path: "/" },

  {
    label: "Vehicles",
    icon: <VehicleIcon />,
    subItems: [
      { label: "Add Vehicle", icon: <AddIcon />, path: "/add-vehicle" },
      { label: "All Vehicles", icon: <AllVehiclesIcon />, path: "/vehicles" },
      { label: "Trash", icon: <TrashIcon />, path: "/trash-vehicles" },
    ],
  },

  { label: "MOT Status", icon: <img src={motIcon} alt="MOT" style={{ width: 24, height: 24 }} />, path: "/upcoming-mots" },
  { label: "PCO Status", icon: <Badge/>, path: "/pco-status" },
  { label: "Booking Requests", icon: <CalendarToday />, path: "/booking-requests" },
  { label: "Agreements", icon: <AgreementIcon />, path: "/agreements" },
  { label: "Invoices", icon: <InvoiceIcon />, path: "/invoices" },
  { label: "Users", icon: <UsersIcon />, path: "/users" },
];

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.authReducer.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [openDrawer, setOpenDrawer] = useState(false);
  const [openSubMenus, setOpenSubMenus] = useState({});

  // Auto open submenu if current path matches
  useEffect(() => {
    const newOpenSubMenus = {};
    menuItems.forEach((item, index) => {
      if (item.subItems) {
        const hasActiveSub = item.subItems.some((sub) => sub.path === location.pathname);
        if (hasActiveSub) newOpenSubMenus[index] = true;
      }
    });
    setOpenSubMenus(newOpenSubMenus);
  }, [location.pathname]);

  const handleToggleSubMenu = (index) => {
    setOpenSubMenus((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) setOpenDrawer(false); // Close drawer on mobile after click
  };

  const doLogout = async () => {
    dispatch(setLoader(true));
    await postRequest({ token: user?.token }, apiRoutes.logout);
    dispatch(setUser({}));
    dispatch(setLoader(false));
    navigate("/");
    setOpenDrawer(false);
  };

  const drawerContent = (
    <Box 
    sx={{ width: 280, height: "100%", display: "flex", flexDirection: "column", bgcolor: "#0044AA", color: "white" }}>
      {/* Logo / Header */}
      <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
  <img src={logo} alt="Logo" style={{ height: 50, objectFit: "contain" }} />
  
  {isMobile && (
    <IconButton onClick={() => setOpenDrawer(false)} sx={{ color: "white" }}>
      <MenuIcon /> {/* Ya Cross icon ke liye <CloseIcon /> use kar sakte ho */}
    </IconButton>
  )}
</Box>

      <Divider sx={{ bgcolor: "rgba(255,255,255,0.2)" }} />

      {/* Menu Items */}
      <List sx={{ flexGrow: 1, px: 1 }}>
        {menuItems.map((item, index) => {
          if (item.subItems) {
            const isOpen = openSubMenus[index];
            const isActive = item.subItems.some((sub) => sub.path === location.pathname);

            return (
              <React.Fragment key={index}>
                <ListItemButton
                  onClick={() => handleToggleSubMenu(index)}
                  sx={{
                    borderRadius: 2,
                    my: 0.5,
                    bgcolor: isActive ? "rgba(255,255,255,0.15)" : "transparent",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  <ListItemIcon sx={{ color: "white", minWidth: 40 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 500 }} />
                  {isOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>

                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems.map((sub, subIdx) => (
                      <ListItemButton
                        key={subIdx}
                        onClick={() => handleNavigation(sub.path)}
                        selected={location.pathname === sub.path}
                        sx={{
                          pl: 6,
                          borderRadius: 2,
                          my: 0.3,
                          bgcolor: location.pathname === sub.path ? "rgba(255,255,255,0.25)" : "transparent",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
                        }}
                      >
                        <ListItemIcon sx={{ color: "white", minWidth: 36 }}>{sub.icon}</ListItemIcon>
                        <ListItemText primary={sub.label} />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          return (
            <ListItemButton
              key={index}
              onClick={() => handleNavigation(item.path)}
              selected={location.pathname === item.path}
              sx={{
                borderRadius: 2,
                my: 0.5,
                bgcolor: location.pathname === item.path ? "rgba(255,255,255,0.2)" : "transparent",
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              <ListItemIcon sx={{ color: "white", minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 500 }} />
            </ListItemButton>
          );
        })}
      </List>

      <Divider sx={{ bgcolor: "rgba(255,255,255,0.2)" }} />

      {/* Logout */}
      <List sx={{ pb: 2 }}>
        <ListItemButton onClick={doLogout} sx={{ borderRadius: 2, mx: 2, my: 1 }}>
          <ListItemIcon sx={{ color: "white", minWidth: 40 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 500 }} />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <div style={{paddingLeft:isMobile? 0:280}} >
      {/* Mobile Toggle Button (only show when mobile & drawer closed) */}
     {isMobile && !openDrawer && (
  <IconButton
    onClick={() => setOpenDrawer(!openDrawer)}  // <-- Ye line change ki hai: !openDrawer
    sx={{
      position: "fixed",
      top: 16,
      left: 16,
      zIndex: 1400, // Drawer se upar rahe (Drawer default zIndex 1300 hota hai)
      bgcolor: "#0044AA",
      color: "white",
      "&:hover": { bgcolor: "#003388" },
      // Jab drawer open ho to button thoda shift ho jaye (optional but nice UX)
      transition: "left 0.3s",
      left: openDrawer ? 296 : 16, // Drawer width 280 + 16 padding
    }}
  >
    <MenuIcon />
  </IconButton>
)}

      {/* Drawer */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? openDrawer : true}
        onClose={() => setOpenDrawer(false)}
        ModalProps={{ keepMounted: true }} // Better mobile performance
        sx={{
          "& .MuiDrawer-paper": {
            bgcolor: "#0044AA",
            color: "white",
            width: 280,
            boxSizing: "border-box",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </div>
  );
};

export default Sidebar;

// import { Sidebar, Menu, MenuItem, SubMenu, sidebarClasses, menuClasses } from "react-pro-sidebar";
// import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
// import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
// import People from "@mui/icons-material/People";
// import Receipt from "@mui/icons-material/Receipt";
// import HandshakeOutlined from "@mui/icons-material/HandshakeOutlined";
// import ReceiptRoundedIcon from "@mui/icons-material/ReceiptRounded";
// import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
// import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
// import BubbleChartRoundedIcon from "@mui/icons-material/BubbleChartRounded";
// import WalletRoundedIcon from "@mui/icons-material/WalletRounded";
// import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
// import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
// import MonetizationOnRoundedIcon from "@mui/icons-material/MonetizationOnRounded";
// import SettingsApplicationsRoundedIcon from "@mui/icons-material/SettingsApplicationsRounded";
// import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
// import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
// import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
// import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
// import DriveEtaIcon from '@mui/icons-material/DriveEta';
// import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
// import LoupeIcon from '@mui/icons-material/Loupe';
// import NoCrashSharpIcon from '@mui/icons-material/NoCrashSharp';
// import Delete from '@mui/icons-material/Delete';
// import '../Sidebar/sidebar.css';
// import { ThemeProvider, createTheme } from '@mui/material/styles';
// import { Container, Typography, useMediaQuery } from "@mui/material";
// import React, { useState } from "react";
// import appColors from "../../config/appColors";
// import { Navigate, useLocation } from "react-router-dom";
// import { useNavigate } from 'react-router-dom';
// import logo from '../../images/headerlogin.png';
// import { useDispatch, useSelector } from "react-redux";
// import { postRequest } from "../../Api";
// import { setLoader } from "../../Redux/Reducers/gernalSlice";
// import { apiRoutes } from "../../Api/apiRoutes";
// import { setUser } from "../../Redux/Reducers/authSlice";
// import useWindowDimensions from "../../config/useWindowDimensions";
// import motIcon from '../../images/motIcon.png';
// import { Settings } from "@mui/icons-material";





// const Sidebars = (props) => {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const { height, width } = useWindowDimensions();


//   const user = useSelector((state) => state.authReducer.user);
//   const dispatch = useDispatch();

//   const [mobileOpen, setMobileOpen] = React.useState(false);
//   const [isClosing, setIsClosing] = React.useState(false);
//   const [openKeys, setOpenKeys] = useState([]);

//   const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

//   const sideBarLabels = {
//     fontFamily: 'Poppins',
//     fontSize: 16,
//     backgroundColor: '#0044AA',
//     color: 'white'
//   }


//   const handleDrawerTransitionEnd = () => {
//     setIsClosing(false);
//   };

//   const handleDrawerToggle = () => {
//     if (!isClosing) {
//       setMobileOpen(!mobileOpen);
//     }
//   };

//   const handleOpenSubMenu = (keys) => {
//     setOpenKeys(keys);
//   };


//   const handleButtonClick = (sidelink) => {
//     switch (sidelink) {
//       case 'dashboard':
//         navigate('/');
//         break;

//       case 'addvehicle':
//         navigate('/add-vehicle');
//         break;

//       case 'allvehicle':
//         navigate('/vehicles');
//         break;
//       case 'trash':
//         navigate('/trash-vehicles');

//       default:
//         // Handle other cases or provide a default route
//         console.error('Unknown sidelink:', sidelink);
//     }
//   };

//   const doLogout = async () => {

//     const onSuccess = () => {
//     }
//     const onError = (error) => {
//     }
//     const data = {
//       token: user?.token
//     }
//     dispatch(setLoader(true))
//     await postRequest(data, apiRoutes.logout, onSuccess, onError);
//     dispatch(setUser({}));
//     navigate('/');

//   }

//   const shouldSubMenuBeOpen = () => {
//     const { pathname } = location;
//     return pathname.includes('vehicles') || pathname.includes('add-vehicle');
//   };

//   const getActiveBgColor = (path) => {
//     return location.pathname === path ? appColors.primaryLight : appColors.primary;
//   };


//   React.useEffect(() => {
//     if (shouldSubMenuBeOpen()) {
//       setOpenKeys(['vehicles']);
//     } else {
//       setOpenKeys([]);
//     }


//   }, [location]);


//   return (
//     <div style={{ display: "flex", minHeight: height,maxHeight:height*2,backgroundColor: appColors.primary,}}>

//       <Sidebar

//         rootStyles={{
//           [`.${sidebarClasses.container}`]: {
//             backgroundColor: appColors.primary,
//             // maxHeight:100000,
//             // minHeight:'100%'
//           },
//         }}

//         // backgroundColor={appColors.primary}
//         className={`app ${isMobile ? "mobile-sidebar" : ""}`}
//         collapsed={isMobile && !mobileOpen}
//         onTransitionEnd={handleDrawerTransitionEnd}
//       >


//         {/* Header of sidebar */}
//         <Menu>
//           <MenuItem
//             style={{ backgroundColor: appColors.primary }}
//             className="menu1" icon={
//               isMobile ? (
//                 <MenuRoundedIcon onClick={handleDrawerToggle} />
//               ) : (
//                 <img src={logo} alt="logo" style={{ display: 'flex', width: 200, marginLeft: 150 }} />
//               )
//             }>
//           </MenuItem>
//         </Menu>
//         <Menu
//           className="menu-item">

//           <MenuItem
//             style={sideBarLabels}
//             onClick={() => handleButtonClick('dashboard')}
//             icon={<GridViewRoundedIcon />}> Dashboard </MenuItem>
//           {/* <MenuItem icon={<DriveEtaIcon />}> Vehicles </MenuItem> */}

//           <SubMenu
//             style={{ ...sideBarLabels }}
//             open={shouldSubMenuBeOpen()}
//             label="Vehicles"
//             icon={<DriveEtaIcon />}>
//             <MenuItem
//               style={{ ...sideBarLabels, backgroundColor: getActiveBgColor('/add-vehicle') }}
//               // style={sideBarLabels}
//               onClick={() => handleButtonClick('addvehicle')}
//               icon={<LoupeIcon />}> Add Vehicles </MenuItem>
//             <MenuItem
//               style={{ ...sideBarLabels, backgroundColor: getActiveBgColor('/vehicles') }}
//               onClick={() => handleButtonClick('allvehicle')}
//               icon={<NoCrashSharpIcon />}>All Vehicles</MenuItem>
//             <MenuItem
//               style={{ ...sideBarLabels, backgroundColor: getActiveBgColor('/trash-vehicles') }}
//               onClick={() => handleButtonClick('trash')}
//               icon={<Delete />}>Trash</MenuItem>
//           </SubMenu>
          
//           <MenuItem
//             style={sideBarLabels}
//             onClick={() => navigate('/upcoming-mots')}
//             icon={<img style={{width:30,backgroundColor:'red'}} src={motIcon} />}>Upcoming MOTs </MenuItem>

//           <MenuItem
//             style={sideBarLabels}
//             onClick={() => navigate('/booking-requests')}
//             icon={<GridViewRoundedIcon />}>Booking requests </MenuItem>
//           <MenuItem
//             style={sideBarLabels}
//             onClick={() => navigate('/agreements')}
//             icon={<HandshakeOutlined />}>Agreements </MenuItem>
//           <MenuItem
//             style={sideBarLabels}
//             onClick={() => navigate('/invoices')}
//             icon={<Receipt />}>Invoices </MenuItem>
//           <MenuItem
//             style={sideBarLabels}
//             onClick={() => navigate('/users')}
//             icon={<People />}>Users </MenuItem>
//           {/* <MenuItem
//             style={sideBarLabels}
//             onClick={() => navigate('/privacy')}
//             icon={<Settings />}>Settings </MenuItem> */}




//           <MenuItem
//             onClick={() => doLogout()}
//             style={sideBarLabels}
//             icon={<LogoutRoundedIcon />}> Logout </MenuItem>
//         </Menu>
//       </Sidebar>

//     </div>
//   );
// };

// export default Sidebars;
